package com.eladfinish.bible.contest.screens.completeVerse.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextLayoutResult
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDirection
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.parchment_verse
import com.eladfinish.bible.contest.screens.completeVerse.model.VerseWithBlanks
import com.eladfinish.bible.contest.text.SpanType
import com.eladfinish.bible.contest.text.SpannableStringKMP
import com.eladfinish.bible.contest.ui.text.toAnnotatedWithUnderscorePlaceholders
import dev.icerock.moko.resources.compose.painterResource

private const val TAG_BLANK_CLICK = "blank_click"

@Composable
internal fun CompleteVerseParchmentView(
    verseWithBlanks: VerseWithBlanks,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .wrapContentHeight(),
        contentAlignment = Alignment.Center,
    ) {
        Image(
            painter = painterResource(SharedRes.images.parchment_verse),
            contentDescription = null,
            contentScale = ContentScale.FillBounds,
            modifier = Modifier.matchParentSize(),
        )
        ClickableVerseText(
            spannableString = verseWithBlanks.spannableStringKMP,
            modifier = Modifier.padding(start = 36.dp, end = 42.dp, top = 16.dp, bottom = 24.dp),
        )
    }
}

@Composable
private fun ClickableVerseText(
    spannableString: SpannableStringKMP,
    modifier: Modifier = Modifier,
) {
    val rawText = spannableString.charSequence.toString()

    val clickActions = remember(spannableString) {
        spannableString.spannedRangeList
            .filter { it.spanType is SpanType.ClickableLegacy }
            .associate { range -> range.textRange.start to (range.spanType as SpanType.ClickableLegacy).onClick }
    }

    // Handles underscore rendering per-platform: Serif font on Android, underlined spaces on iOS.
    // On iOS each '_' is expanded to IOS_UNDERSCORE_EXPANSION_FACTOR characters, so all
    // character positions in `base` after the first blank are shifted relative to `rawText`.
    val base = rawText.toAnnotatedWithUnderscorePlaceholders()

    // Overlay click annotations + color spans from SpannableStringKMP on top of the base.
    // iOS now uses EM DASH (1-to-1 substitution) so base has the same length as rawText
    // and all span positions from spannedRangeList map directly — no remapping needed.
    val annotated = remember(spannableString, base) {
        buildAnnotatedString {
            append(base)
            spannableString.spannedRangeList.forEach { range ->
                val start = range.textRange.start.coerceAtLeast(0)
                val end = range.textRange.end.coerceAtMost(rawText.length)
                if (start >= end) return@forEach
                when (val type = range.spanType) {
                    is SpanType.ClickableLegacy ->
                        addStringAnnotation(TAG_BLANK_CLICK, start.toString(), start, end)

                    is SpanType.BackgroundColor ->
                        addStyle(SpanStyle(background = type.color), start, end)

                    is SpanType.ForegroundColor ->
                        addStyle(SpanStyle(color = type.color), start, end)

                    else -> Unit
                }
            }
        }
    }

    var textLayoutResult by remember { mutableStateOf<TextLayoutResult?>(null) }

    Text(
        text = annotated,
        fontSize = 21.sp,
        color = Color.Black,
        style = MaterialTheme.typography.bodyLarge.copy(
            textDirection = TextDirection.Content,
            fontSize = 16.sp,
            color = Color.Black,
            lineHeight = 28.sp,
        ),
        modifier = modifier.pointerInput(clickActions, annotated) {
            detectTapGestures(
                onTap = { offset ->
                    val layout = textLayoutResult ?: return@detectTapGestures
                    val position = layout.getOffsetForPosition(offset)
                    val annotation = annotated
                        .getStringAnnotations(TAG_BLANK_CLICK, position, position)
                        .firstOrNull()
                        ?: return@detectTapGestures
                    val start = annotation.item.toIntOrNull() ?: return@detectTapGestures
                    clickActions[start]?.invoke()
                },
            )
        },
        textAlign = TextAlign.Center,
        onTextLayout = { textLayoutResult = it },
    )
}
